import { useState, useEffect, useMemo, useCallback } from 'react';
import { PartnerOption, StatusOption } from '../types/deliveryHistory.types';
import { PartnerRawItem, PartnerListApiResponse } from '../types/deliveryHistory.api.types';
import { deliveryHistoryApi } from '../api/deliveryHistoryApi';
import { toSafeString } from '@/shared/utils/apiResponseHelpers';

const STATUS_PRIORITY_KEYWORDS = ['운행중', '운행종료', '종료'] as const;

const sortStatusCodes = (codes: StatusOption[]): StatusOption[] => {
    if (!codes?.length) return [];
    return [...codes].sort((a, b) => {
        for (const keyword of STATUS_PRIORITY_KEYWORDS) {
            const aHas = a.name.includes(keyword);
            const bHas = b.name.includes(keyword);
            if (aHas && !bHas) return -1;
            if (!aHas && bHas) return 1;
        }
        return a.name.localeCompare(b.name);
    });
};

const normalizePartnerList = (response: PartnerListApiResponse): PartnerOption[] => {
    let list: PartnerRawItem[] = [];
    if (Array.isArray(response)) {
        list = response;
    } else if (typeof response === 'object' && response !== null) {
        list =
            (response as { suppliers?: PartnerRawItem[] }).suppliers ||
            (response as { partners?: PartnerRawItem[] }).partners ||
            (response as { data?: PartnerRawItem[] }).data ||
            [];
    }
    return list
        .filter((item): item is PartnerRawItem => !!(item?.suppId || item?.suppName))
        .map((item) => ({
            suppId: toSafeString(item.suppId),
            suppName: toSafeString(item.suppName),
        }));
};

export const useDeliveryOptions = (userType: string | null) => {
    const [partners, setPartners] = useState<PartnerOption[]>([]);
    const [partnersLoading, setPartnersLoading] = useState(false);
    const [statusCodes, setStatusCodes] = useState<StatusOption[]>([]);
    const [statusLoading, setStatusLoading] = useState(false);

    const fetchStatusCodes = useCallback(async () => {
        setStatusLoading(true);
        try {
            const response = await deliveryHistoryApi.getStatusCodes();
            setStatusCodes(response || []);
        } catch {
            setStatusCodes([]);
        } finally {
            setStatusLoading(false);
        }
    }, []);

    const fetchPartners = useCallback(async () => {
        if (userType !== 'HEADQUARTERS') return;
        setPartnersLoading(true);
        try {
            const response = await deliveryHistoryApi.getPartnerList();
            setPartners(normalizePartnerList(response));
        } catch {
            setPartners([]);
        } finally {
            setPartnersLoading(false);
        }
    }, [userType]);

    useEffect(() => {
        fetchStatusCodes();
        fetchPartners();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const sortedStatusCodes = useMemo(() => sortStatusCodes(statusCodes), [statusCodes]);

    return { partners, partnersLoading, statusCodes: sortedStatusCodes, statusLoading };
};
